/**
 * Proposals and follow-ups — the CRM's outbound half. Spec §H3, §H4, §H5.
 *
 * **The acceptance link is returned once and never again.** `send` answers with
 * it because that is the one moment the firm needs to copy it; every later read
 * carries `has_link` and nothing else. It is a capability — anybody holding it
 * can accept a proposal on the firm's domain — so a list render that put one in
 * a browser's network log would be handing it out for nothing.
 *
 * **A proposal is governed by `lead`.** `proposal` is not one of the fourteen
 * resources T-1.4 publishes, and waves.md §1.2 says to use the resource that
 * exists rather than edit `rbac.py` from another session. It is also the
 * truthful answer: a proposal is a document in the pipeline, and who may see a
 * firm's leads is who may see what it quoted them.
 */

import { api } from '../api'

/** FR-H4's five. `expired` is what the clock does, not what a person does. */
export type ProposalStatus = 'draft' | 'sent' | 'accepted' | 'declined' | 'expired'

/** One line of the quote, as the **service's snapshot** holds it. Amounts are
 *  strings: a rupee amount that has been through a JavaScript `number` is a
 *  rupee amount that may have been rounded (plan §2, "Money"). */
export interface ProposalLine {
  line_no: number
  service_id: string | null
  service_name: string | null
  plan_id: string | null
  plan_name: string | null
  description: string
  sac_code: string | null
  gst_treatment: string | null
  gst_rate: string | null
  quantity: string
  unit_amount: string
  amount: string
}

export interface ProposalRecord {
  id: string
  lead_id: string | null
  client_id: string | null
  number: string | null
  title: string | null
  status: ProposalStatus
  lines: ProposalLine[]
  currency: string
  total_amount: string | null
  valid_until: string | null
  /** Answered by the **server** against its own clock and the firm's own day.
   *  A screen deriving it from `valid_until` would be a day out for half of
   *  every evening. */
  is_expired: boolean
  sent_at: string | null
  decided_at: string | null
  /** Whether a link has been minted. **Never the link itself** — see above. */
  has_link: boolean
  /** Read back off the lead's trail. Null unless the proposal was declined. */
  decline_reason: string | null
  created_at: string
  /** Present **only** on the answer to `send`. */
  link?: string
}

export interface SendResult {
  proposal: ProposalRecord
  /** False while `prodesk.proposal.sent` is unregistered in
   *  `core-communication`, which 404s an unknown template. Wave 5F shipped the
   *  wording; **registering it is a platform ask and it is still open**. The
   *  send fails closed, the link still exists, and the screen says so rather
   *  than claiming an email went out. */
  delivered: boolean
  channel: string | null
  templateCode: string
}

export interface FollowUp {
  id: string
  scheduled_at: string
  assignee_user_id: string | null
  note: string | null
  status: 'scheduled' | 'done' | 'missed' | 'cancelled'
  outcome: string | null
  /** The server's answer. A call back whose time has gone is *late*; `missed`
   *  is somebody saying nobody picked up (FR-E10). */
  is_late: boolean
  completed_at: string | null
}

export interface LineInput {
  plan_id?: string
  description?: string
  amount?: string
  quantity?: string
}

export const proposals = {
  list: (params: { status?: ProposalStatus | ''; lead_id?: string; limit?: number } = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<ProposalRecord[]>(`/api/v1/proposals${suffix}`)
  },

  get: (id: string) => api<ProposalRecord>(`/api/v1/proposals/${id}`),

  vocabulary: () =>
    api<{ statuses: ProposalStatus[]; decisions: string[]; accept_path: string }>(
      '/api/v1/proposals/vocabulary',
    ),

  create: (body: {
    lead_id?: string
    client_id?: string
    title?: string
    lines: LineInput[]
    valid_until?: string | null
  }) => api<ProposalRecord>('/api/v1/proposals', { method: 'POST', body }),

  /** A **draft** only. The service refuses a sent one: the link a prospect
   *  holds and the document they are reading are the same object. */
  update: (
    id: string,
    body: Partial<{ title: string; lines: LineInput[]; valid_until: string | null }>,
  ) => api<ProposalRecord>(`/api/v1/proposals/${id}`, { method: 'PATCH', body }),

  /**
   * Number it, mint the link, move the lead, ask core to deliver.
   *
   * `origin` is the firm's own website address. The **service** cannot discover
   * it — `core-domain-whitelisting` answers host → org and never org → host —
   * so it defaults to the firm's own `custom_domain` and is refused when there
   * is neither, rather than sending an email whose link points nowhere.
   */
  send: async (id: string, origin?: string): Promise<SendResult> => {
    const body = await api<ProposalRecord>(`/api/v1/proposals/${id}/send`, {
      method: 'POST',
      body: { origin: origin ?? null },
    })
    const meta = body.meta ?? {}
    return {
      proposal: body.data,
      delivered: meta.delivered === true,
      channel: typeof meta.channel === 'string' ? meta.channel : null,
      templateCode:
        typeof meta.template_code === 'string' ? meta.template_code : 'prodesk.proposal.sent',
    }
  },

  /** FR-H3 — a call back on a lead, through 4A's own engine. */
  followUps: (leadId: string) => api<FollowUp[]>(`/api/v1/leads/${leadId}/follow-ups`),

  scheduleFollowUp: (
    leadId: string,
    body: { scheduled_at: string; assignee_user_id?: string; note?: string },
  ) =>
    api<{ callback_id: string; scheduled_at: string; next_follow_up_on: string | null }>(
      `/api/v1/leads/${leadId}/follow-ups`,
      { method: 'POST', body },
    ),
}

// -- pure helpers, so the screen's rules are testable -------------------------

export const PROPOSAL_STATUSES: ProposalStatus[] = [
  'draft',
  'sent',
  'accepted',
  'declined',
  'expired',
]

export const STATUS_LABELS: Record<ProposalStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
}

/** `12000.00` → `₹12,000.00`, grouped the Indian way — `1,200,000` is not how a
 *  lakh is written, and the audience is an Indian firm. */
export function money(amount: string | null | undefined, currency = 'INR'): string {
  if (!amount) return '—'
  const value = Number(amount)
  if (!Number.isFinite(value)) return amount
  const symbol = currency === 'INR' ? '₹' : `${currency} `
  return (
    symbol +
    value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  )
}

/**
 * Whether the firm may still send this one.
 *
 * A draft and a sent-but-unanswered proposal both may — re-sending is a chase,
 * and the service keeps the same number and the same link so the prospect's copy
 * never stops working. An answered or expired one may not, and hiding the
 * control is the UI half of a rule the service enforces (FiveOX ADR-0029).
 */
export function canSend(row: ProposalRecord): boolean {
  if (row.status === 'accepted' || row.status === 'declined' || row.status === 'expired') {
    return false
  }
  return !row.is_expired
}

/** A draft is the only editable state, for the reason `canSend` gives. */
export function canEdit(row: ProposalRecord): boolean {
  return row.status === 'draft'
}

/** One line under a proposal: who it went to and where it stands. */
export function proposalSubtitle(row: ProposalRecord): string {
  const parts = [
    row.number,
    money(row.total_amount, row.currency),
    row.valid_until && row.status === 'sent' ? `valid to ${row.valid_until}` : null,
  ]
  return parts.filter(Boolean).join(' · ')
}

/**
 * What the screen says about the send, in the firm's language.
 *
 * `delivered: false` is **not an error** and must not read as one: the proposal
 * exists, it is numbered, and its link works. What is missing is the template
 * registration in `core-communication` — a platform ask 5F opened and nobody
 * has granted — so the honest sentence is
 * "copy the link" rather than "sending failed".
 */
export function sendNote(result: SendResult): string {
  return result.delivered
    ? 'Sent. The prospect has the link.'
    : 'The proposal is ready and its link works — but we could not email it yet. Copy the link below and send it yourself.'
}
